import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Pencil,
  Loader2,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  PlusCircle,
  Trash2,
  ChevronRight,
  UserCheck,
  Users,
  Workflow,
  Star,
  Info,
  UserPlus,
} from 'lucide-react';
import { leaveService } from '@/services/leave.service';
import { usersService, type PickerUser } from '@/services/users.service';
import { toast } from 'sonner';
import type { LeaveType, LeaveApprovalFlow, ShortLeaveApprovalFlow } from '@pms/shared';

const ROLES = ['MANAGER', 'TEAM_LEADER', 'ADMIN', 'SUPER_ADMIN'] as const;
type ApproverRole = typeof ROLES[number];
type ApprovalMethod = 'self' | 'senior' | 'role_based' | 'any_of_role' | 'any_one';

interface FlowStep {
  step_order: number;
  approver_role: ApproverRole;
  is_final: boolean;
}

const ROLE_LABELS: Record<ApproverRole, string> = {
  MANAGER: 'Manager',
  TEAM_LEADER: 'Team Leader',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
};

const ROLE_COLORS: Record<ApproverRole, string> = {
  MANAGER:     'bg-teal-50 text-teal-700 border-teal-200',
  TEAM_LEADER: 'bg-violet-50 text-violet-700 border-violet-200',
  ADMIN:       'bg-teal-50 text-teal-700 border-teal-200',
  SUPER_ADMIN: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Checkbox list of staff for the "Any One Approver" method, with a search box.
 *
 * Regular leave and short leave each render one of these, so the filtering lives here
 * once rather than in two copies that can drift. Each instance owns its own search
 * text — typing in one must not filter the other.
 *
 * Deliberately inline rather than in a Popover: a Radix popover portals outside the
 * dialog's focus trap and the search input loses focus on every keystroke. The goals
 * module hit this and documents it in GoalAssigneeSelect.
 */
function ApproverPicker({
  staff,
  selected,
  onChange,
  isError,
  onRetry,
}: {
  staff: PickerUser[] | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  isError: boolean;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState('');

  // No is_active filter here — /users/picker already returns active staff only, so
  // someone who has left can never be picked as an approver.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff ?? [];
    return (staff ?? []).filter(
      (u) =>
        `${u.first_name} ${u.last_name ?? ''}`.toLowerCase().includes(q) ||
        (u.employee_code ?? '').toLowerCase().includes(q),
    );
  }, [staff, search]);

  if (isError) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive py-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />Failed to load staff.
        <button className="underline text-xs" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (!staff) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
        <Loader2 className="w-4 h-4 animate-spin" />Loading staff…
      </div>
    );
  }

  return (
    <>
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search by name or employee code…"
      />
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          {search.trim()
            ? `No staff match "${search.trim()}".`
            : 'No staff members found.'}
        </p>
      ) : (
        <div className="max-h-52 overflow-y-auto rounded-lg border border-border/60 divide-y divide-border/40">
          {visible.map((u) => {
            const checked = selected.includes(u.id);
            return (
              <label
                key={u.id}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange(checked ? selected.filter((id) => id !== u.id) : [...selected, u.id])
                  }
                  className="accent-teal-600 w-4 h-4 shrink-0"
                />
                <span className="text-sm flex-1">{u.first_name} {u.last_name}</span>
                <span className="text-xs text-muted-foreground">{u.employee_code}</span>
              </label>
            );
          })}
        </div>
      )}
      {/* Selections are held by id, so they survive filtering — this count is the only
          thing telling the admin that someone ticked behind a since-cleared search is
          still selected. */}
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">{selected.length} approver(s) selected</p>
      )}
    </>
  );
}

const METHOD_META: Record<ApprovalMethod, {
  label: string;
  description: string;
  icon: React.ReactNode;
  badgeCls: string;
}> = {
  self: {
    label: 'Self Approve',
    description: 'Leave is approved automatically the moment the employee applies. No approver.',
    icon: <UserCheck className="w-5 h-5" />,
    badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  senior: {
    label: 'Senior / Manager',
    description: 'Any Manager, Admin or Super Admin can approve. One approval is final.',
    icon: <Users className="w-5 h-5" />,
    badgeCls: 'bg-teal-50 text-teal-700 border-teal-200',
  },
  role_based: {
    label: 'Role-Based Flow',
    description: 'Define a custom multi-step approver chain by role.',
    icon: <Workflow className="w-5 h-5" />,
    badgeCls: 'bg-teal-50 text-teal-700 border-teal-200',
  },
  any_of_role: {
    label: 'Any of Role',
    description: 'Any user holding the selected role can approve.',
    icon: <Star className="w-5 h-5" />,
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  any_one: {
    label: 'Any One Approver',
    description: 'Leave is approved when any one of the assigned approvers approves it.',
    icon: <UserPlus className="w-5 h-5" />,
    badgeCls: 'bg-violet-50 text-violet-700 border-violet-200',
  },
};

function FlowStepBadge({ role }: { role: ApproverRole }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_COLORS[role]}`}>
      {ROLE_LABELS[role]}
    </span>
  );
}

function MethodBadge({ method }: { method: ApprovalMethod }) {
  const m = METHOD_META[method];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${m.badgeCls}`}>
      {m.label}
    </span>
  );
}

function FlowDisplay({ flows, typeId }: { flows: Record<string, LeaveApprovalFlow[]>; typeId: string }) {
  const steps = (flows[typeId] ?? []).sort((a, b) => a.step_order - b.step_order);
  if (steps.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {steps.map((s, i) => (
        <div key={s.id} className="flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground shrink-0">
            {s.step_order}
          </span>
          <FlowStepBadge role={s.approver_role as ApproverRole} />
          {s.is_final && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold border border-primary/20">
              <CheckCircle2 className="w-2.5 h-2.5" />Final
            </span>
          )}
          {i < steps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
        </div>
      ))}
    </div>
  );
}

const SUB_TABS = [
  { key: 'regular', label: 'Regular Leave' },
  { key: 'short',   label: 'Short Leave' },
] as const;
type FlowTab = typeof SUB_TABS[number]['key'];

export function LeaveApprovalFlowSection() {
  const queryClient = useQueryClient();

  // ── sub-tabs ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<FlowTab>('regular');

  // ── regular leave dialog state ───────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<LeaveType | null>(null);
  const [method, setMethod] = useState<ApprovalMethod>('role_based');
  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [anyOfRole, setAnyOfRole] = useState<ApproverRole>('MANAGER');
  const [anyOneApprovers, setAnyOneApprovers] = useState<string[]>([]);

  // ── short leave flow state ───────────────────────────────────────────────────
  const [shortEditing, setShortEditing] = useState(false);
  const [shortSteps, setShortSteps] = useState<FlowStep[]>([]);
  type ShortMethod = 'role_based' | 'any_one';
  const [shortMethod, setShortMethod] = useState<ShortMethod>('role_based');
  const [shortAnyOneApprovers, setShortAnyOneApprovers] = useState<string[]>([]);

  const typesQuery = useQuery({
    queryKey: ['leave', 'types'],
    queryFn: () => leaveService.listLeaveTypes(),
  });

  const flowsQuery = useQuery({
    queryKey: ['leave', 'flows'],
    queryFn: () => leaveService.listApprovalFlows(),
  });

  const anyOneQuery = useQuery({
    queryKey: ['leave', 'any-one-approvers'],
    queryFn: () => leaveService.listAnyOneApprovers(),
  });

  // /users/picker, not /users?limit=100: the paginated list caps at 100 rows (the zod
  // schema forbids more) sorted newest-first, so the org's longest-serving staff fell off
  // the end and could not be chosen as approvers at all — BANSI GAJERA (37) sat at row
  // 111 of 118. The picker endpoint returns every ACTIVE user up to 500, already
  // excluding people who have left.
  const staffQuery = useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => usersService.picker(),
  });

  const shortFlowsQuery = useQuery({
    queryKey: ['leave', 'short-flows'],
    queryFn: () => leaveService.listShortLeaveFlows(),
  });

  const shortAnyOneQuery = useQuery({
    queryKey: ['leave', 'short-any-one-approvers'],
    queryFn: () => leaveService.listShortLeaveAnyOneApprovers(),
  });

  const shortSaveMutation = useMutation({
    mutationFn: async () => {
      if (shortMethod === 'any_one') {
        await leaveService.setShortLeaveAnyOneApprovers(shortAnyOneApprovers);
      } else {
        await leaveService.setShortLeaveFlow(
          shortSteps.map((s) => ({
            step_order: s.step_order,
            approver_role: s.approver_role,
            is_final: s.is_final,
          })),
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'short-flows'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'short-any-one-approvers'] });
      toast.success('Short leave approval flow saved');
      setShortEditing(false);
      setShortAnyOneApprovers([]);
    },
    onError: () => toast.error('Failed to save short leave approval flow'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      await leaveService.updateLeaveType(selectedType!.id, { approval_method: method });

      let flowSteps: FlowStep[] = [];
      if (method === 'role_based') {
        flowSteps = steps;
      } else if (method === 'any_of_role') {
        flowSteps = [{ step_order: 1, approver_role: anyOfRole, is_final: true }];
      }
      await leaveService.setApprovalFlow(selectedType!.id, flowSteps);
      if (method === 'any_one') {
        await leaveService.setAnyOneApprovers(selectedType!.id, anyOneApprovers);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'types'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'flows'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'any-one-approvers'] });
      toast.success('Approval flow saved');
      handleClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Failed to save approval flow';
      toast.error(msg);
    },
  });

  function openEdit(type: LeaveType) {
    setSelectedType(type);
    const currentMethod: ApprovalMethod = (type.approval_method as ApprovalMethod) ?? 'role_based';
    setMethod(currentMethod);

    const existing = (flowsQuery.data?.[type.id] ?? [])
      .sort((a, b) => a.step_order - b.step_order)
      .map((f) => ({
        step_order: f.step_order,
        approver_role: f.approver_role as ApproverRole,
        is_final: f.is_final,
      }));

    if (currentMethod === 'any_of_role' && existing.length > 0) {
      setAnyOfRole(existing[0]!.approver_role);
      setSteps([]);
      setAnyOneApprovers([]);
    } else if (currentMethod === 'role_based') {
      setSteps(existing.length > 0 ? existing : [{ step_order: 1, approver_role: 'MANAGER', is_final: true }]);
      setAnyOneApprovers([]);
    } else if (currentMethod === 'any_one') {
      setAnyOneApprovers(anyOneQuery.data?.[type.id] ?? []);
      setSteps([]);
    } else {
      setSteps([]);
      setAnyOneApprovers([]);
    }
    setDialogOpen(true);
  }

  function handleClose() {
    setDialogOpen(false);
    setSelectedType(null);
    setSteps([]);
    setAnyOneApprovers([]);
  }

  function addStep() {
    const nextOrder = steps.length > 0 ? Math.max(...steps.map((s) => s.step_order)) + 1 : 1;
    setSteps((prev) => {
      const updated = prev.map((s) => ({ ...s, is_final: false }));
      return [...updated, { step_order: nextOrder, approver_role: 'MANAGER' as ApproverRole, is_final: true }];
    });
  }

  function removeStep(idx: number) {
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_order: i + 1 }));
      if (next.length > 0) next[next.length - 1]!.is_final = true;
      return next;
    });
  }

  function updateRole(idx: number, role: ApproverRole) {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, approver_role: role } : s)));
  }

  function setFinal(idx: number) {
    setSteps((prev) => prev.map((s, i) => ({ ...s, is_final: i === idx })));
  }

  function startEditShort() {
    const anyOneIds = shortAnyOneQuery.data ?? [];
    if (anyOneIds.length > 0) {
      setShortMethod('any_one');
      setShortAnyOneApprovers(anyOneIds);
      setShortSteps([]);
    } else {
      setShortMethod('role_based');
      setShortAnyOneApprovers([]);
      const existing = (shortFlowsQuery.data ?? []).map((f: ShortLeaveApprovalFlow) => ({
        step_order: f.step_order,
        approver_role: f.approver_role as ApproverRole,
        is_final: f.is_final,
      }));
      setShortSteps(existing.length > 0 ? existing : [{ step_order: 1, approver_role: 'MANAGER' as ApproverRole, is_final: true }]);
    }
    setShortEditing(true);
  }

  function handleShortMethodChange(m: ShortMethod) {
    setShortMethod(m);
    if (m === 'role_based') {
      setShortAnyOneApprovers([]);
      setShortSteps([{ step_order: 1, approver_role: 'MANAGER', is_final: true }]);
    } else {
      setShortSteps([]);
    }
  }

  function addShortStep() {
    const nextOrder = shortSteps.length > 0 ? Math.max(...shortSteps.map((s) => s.step_order)) + 1 : 1;
    setShortSteps((prev) => {
      const updated = prev.map((s) => ({ ...s, is_final: false }));
      return [...updated, { step_order: nextOrder, approver_role: 'MANAGER' as ApproverRole, is_final: true }];
    });
  }

  function removeShortStep(idx: number) {
    setShortSteps((prev) => {
      const next = prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_order: i + 1 }));
      if (next.length > 0) next[next.length - 1]!.is_final = true;
      return next;
    });
  }

  function updateShortRole(idx: number, role: ApproverRole) {
    setShortSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, approver_role: role } : s)));
  }

  function setShortFinal(idx: number) {
    setShortSteps((prev) => prev.map((s, i) => ({ ...s, is_final: i === idx })));
  }

  function handleMethodChange(m: ApprovalMethod) {
    setMethod(m);
    if (m === 'role_based') {
      setSteps([{ step_order: 1, approver_role: 'MANAGER', is_final: true }]);
      setAnyOneApprovers([]);
    } else if (m === 'any_one') {
      setSteps([]);
    } else {
      setSteps([]);
      setAnyOneApprovers([]);
    }
  }

  const types = typesQuery.data ?? [];
  const flows = flowsQuery.data ?? {};

  const canSave =
    method === 'self' ||
    method === 'senior' ||
    (method === 'role_based' && steps.length > 0) ||
    method === 'any_of_role' ||
    (method === 'any_one' && anyOneApprovers.length > 0);

  const columns: Column<LeaveType>[] = [
    {
      key: 'leave_type',
      header: 'Leave Type',
      cell: (t) => (
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
          <span className="font-medium">{t.name}</span>
        </div>
      ),
    },
    {
      key: 'method',
      header: 'Approval Method',
      cell: (t) => {
        const m = (t.approval_method as ApprovalMethod) ?? 'role_based';
        return <MethodBadge method={m} />;
      },
    },
    {
      key: 'flow',
      header: 'Flow Details',
      cell: (t) => {
        const m = (t.approval_method as ApprovalMethod) ?? 'role_based';
        if (m === 'self') return <span className="text-xs text-muted-foreground">Auto-approved</span>;
        if (m === 'senior') return <span className="text-xs text-muted-foreground">Any Manager / Admin</span>;
        if (m === 'any_of_role') {
          const step = flows[t.id]?.[0];
          return step ? (
            <span className="text-xs text-muted-foreground">Any {ROLE_LABELS[step.approver_role as ApproverRole]}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <AlertTriangle className="w-3.5 h-3.5" />Not configured
            </span>
          );
        }
        if (m === 'any_one') {
          const ids = anyOneQuery.data?.[t.id] ?? [];
          const allStaff = staffQuery.data ?? [];
          const names = ids
            .map((id) => allStaff.find((u) => u.id === id))
            .filter(Boolean)
            .map((u) => `${u!.first_name} ${u!.last_name}`);
          // An id that resolves to nobody is an approver who has left, or one the staff
          // list did not return. Saying "Not configured" for a type that HAS approvers
          // saved would be a lie, so count them instead of dropping them silently.
          const unresolved = ids.length - names.length;
          if (ids.length === 0) {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="w-3.5 h-3.5" />Not configured
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {names.length > 0 && (
                <span>
                  {names.slice(0, 2).join(', ')}{names.length > 2 ? ` +${names.length - 2} more` : ''}
                </span>
              )}
              {unresolved > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="w-3.5 h-3.5" />{unresolved} unavailable
                </span>
              )}
            </span>
          );
        }
        const hasFlow = (flows[t.id] ?? []).length > 0;
        if (!hasFlow) {
          return (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />Not configured
            </span>
          );
        }
        return <FlowDisplay flows={flows} typeId={t.id} />;
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'w-28 text-right',
      cell: (t) => (
        <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
          <Pencil className="w-3.5 h-3.5 mr-1.5" />
          Configure
        </Button>
      ),
    },
  ];

  const configuredShortSteps = (shortFlowsQuery.data ?? []).sort((a, b) => a.step_order - b.step_order);
  const configuredShortAnyOne = shortAnyOneQuery.data ?? [];

  const canSaveShort =
    (shortMethod === 'role_based' && shortSteps.length > 0) ||
    (shortMethod === 'any_one' && shortAnyOneApprovers.length > 0);

  return (
    <div className="space-y-4">
      <Tabs
        tabs={SUB_TABS.map((t) => ({ key: t.key, label: t.label }))}
        active={activeTab}
        onChange={(k) => setActiveTab(k as FlowTab)}
      />

      {activeTab === 'short' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-card p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Short Leave Approval Flow</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Defines which role must approve all short leave requests. Applied globally — not per leave type.
                </p>
              </div>
              {!shortEditing && (
                <Button variant="outline" size="sm" onClick={startEditShort}
                  disabled={shortFlowsQuery.isLoading || shortAnyOneQuery.isLoading}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  {(configuredShortSteps.length > 0 || configuredShortAnyOne.length > 0) ? 'Edit Flow' : 'Configure'}
                </Button>
              )}
            </div>

            {shortFlowsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />Loading…
              </div>
            )}

            {shortFlowsQuery.isError && (
              <div className="flex items-center gap-2 text-sm text-destructive py-2">
                <AlertTriangle className="w-4 h-4" />Failed to load flow.
                <button className="underline text-xs" onClick={() => void shortFlowsQuery.refetch()}>Retry</button>
              </div>
            )}

            {!shortEditing && !shortFlowsQuery.isLoading && !shortFlowsQuery.isError && (
              configuredShortAnyOne.length > 0 ? (
                <div className="space-y-2 pt-1">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-violet-50 text-violet-700 border-violet-200">
                    <UserPlus className="w-3.5 h-3.5" />Any One Approver
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {configuredShortAnyOne.map((uid) => {
                      const u = staffQuery.data?.find((s) => s.id === uid);
                      if (!u) return null;
                      return (
                        <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground border border-border/60">
                          {u.first_name} {u.last_name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : configuredShortSteps.length === 0 ? (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-700">
                    No flow configured — any user with the <strong>LEAVE_APPROVE</strong> permission can approve short leave requests.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-teal-50 text-teal-700 border-teal-200">
                    <Workflow className="w-3.5 h-3.5" />Role-Based Flow
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {configuredShortSteps.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-1.5">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground shrink-0">
                          {s.step_order}
                        </span>
                        <FlowStepBadge role={s.approver_role as ApproverRole} />
                        {s.is_final && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold border border-primary/20">
                            <CheckCircle2 className="w-2.5 h-2.5" />Final
                          </span>
                        )}
                        {i < configuredShortSteps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            {shortEditing && (
              <div className="space-y-4 pt-1">
                {/* Method selector */}
                <div className="grid grid-cols-2 gap-2">
                  {(['role_based', 'any_one'] as ShortMethod[]).map((m) => {
                    const meta = m === 'role_based'
                      ? { label: 'Role-Based Flow', desc: 'Any user with the selected role can approve.', icon: <Workflow className="w-5 h-5" /> }
                      : { label: 'Any One Approver', desc: 'Leave approved when any one assigned person approves.', icon: <UserPlus className="w-5 h-5" /> };
                    const active = shortMethod === m;
                    return (
                      <button key={m} type="button" onClick={() => handleShortMethodChange(m)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all ${
                          active ? 'border-teal-500 bg-teal-50/60 ring-1 ring-teal-400/30' : 'border-border/60 bg-muted/20 hover:bg-muted/40'
                        }`}>
                        <span className={`mt-0.5 shrink-0 ${active ? 'text-teal-600' : 'text-muted-foreground'}`}>{meta.icon}</span>
                        <div>
                          <p className={`text-sm font-semibold ${active ? 'text-teal-700' : 'text-foreground'}`}>{meta.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{meta.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Role-based step builder */}
                {shortMethod === 'role_based' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Define Approval Steps
                      </p>
                      {shortSteps.length < 1 && (
                        <Button variant="outline" size="sm" onClick={addShortStep}
                          className="shrink-0 border-dashed text-muted-foreground hover:text-primary hover:border-primary/40">
                          <PlusCircle className="w-4 h-4 mr-1.5" />Add Step
                        </Button>
                      )}
                    </div>

                    {shortSteps.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                        <span className="text-xs font-bold text-muted-foreground w-12 shrink-0">
                          Step {step.step_order}
                        </span>
                        <Select value={step.approver_role} onValueChange={(v) => updateShortRole(idx, v as ApproverRole)}>
                          <SelectTrigger className="flex-1 h-8 text-sm bg-background">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button type="button" onClick={() => setShortFinal(idx)}
                          className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border transition-colors ${
                            step.is_final
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-background text-muted-foreground border-border hover:text-emerald-600'
                          }`}>
                          <CheckCircle2 className="w-3 h-3" />Final
                        </button>
                        {shortSteps.length > 1 && (
                          <button type="button" onClick={() => removeShortStep(idx)}
                            className="text-muted-foreground hover:text-destructive transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}

                    {shortSteps.length > 0 && (
                      <div className="rounded-lg bg-muted/40 border border-border/60 p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Preview:</p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {shortSteps.map((s, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <FlowStepBadge role={s.approver_role} />
                              {i < shortSteps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Any-one user picker */}
                {shortMethod === 'any_one' && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Select Approvers
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Leave is approved when <strong>any one</strong> of these users approves it.
                    </p>
                    <ApproverPicker
                      staff={staffQuery.data}
                      selected={shortAnyOneApprovers}
                      onChange={setShortAnyOneApprovers}
                      isError={staffQuery.isError}
                      onRetry={() => void staffQuery.refetch()}
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => { setShortEditing(false); setShortAnyOneApprovers([]); }}>
                    Cancel
                  </Button>
                  <Button onClick={() => shortSaveMutation.mutate()} disabled={shortSaveMutation.isPending || !canSaveShort} className="flex-1">
                    {shortSaveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Flow'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'regular' && (
        <>
        <DataTable
          columns={columns}
          rows={types}
          rowKey={(t) => t.id}
          loading={typesQuery.isLoading || flowsQuery.isLoading}
          error={typesQuery.isError || flowsQuery.isError}
          onRetry={() => { void typesQuery.refetch(); void flowsQuery.refetch(); }}
          empty={
            <div className="flex flex-col items-center gap-2 py-6">
              <p className="text-sm font-medium text-muted-foreground">No leave types configured</p>
              <p className="text-xs text-muted-foreground/70">
                Create leave types first, then configure their approval flows.
              </p>
            </div>
          }
        />

      <Dialog open={dialogOpen} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approval Flow — {selectedType?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Method selection cards */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Choose Approval Method
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(METHOD_META) as ApprovalMethod[]).map((m) => {
                  const meta = METHOD_META[m];
                  const active = method === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleMethodChange(m)}
                      className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all ${
                        active
                          ? 'border-teal-500 bg-teal-50/60 ring-1 ring-teal-400/30'
                          : 'border-border/60 bg-muted/20 hover:bg-muted/40'
                      }`}
                    >
                      <span className={`mt-0.5 shrink-0 ${active ? 'text-teal-600' : 'text-muted-foreground'}`}>
                        {meta.icon}
                      </span>
                      <div>
                        <p className={`text-sm font-semibold ${active ? 'text-teal-700' : 'text-foreground'}`}>
                          {meta.label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{meta.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Method-specific config */}
            {(method === 'self' || method === 'senior') && (
              <div className="flex items-start gap-2.5 rounded-lg border border-teal-200 bg-teal-50/60 p-4">
                <Info className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                <p className="text-sm text-teal-700">
                  {method === 'self'
                    ? 'No additional configuration needed. Every request of this type is approved automatically as soon as the employee applies.'
                    : 'Any user with the Manager, Admin or Super Admin role can approve or reject these requests (never their own). No steps to configure.'}
                </p>
              </div>
            )}

            {method === 'any_of_role' && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Select Approver Role
                </p>
                <Select value={anyOfRole} onValueChange={(v) => setAnyOfRole(v as ApproverRole)}>
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Any user with the <strong>{ROLE_LABELS[anyOfRole]}</strong> role can approve this leave.
                </p>
              </div>
            )}

            {method === 'any_one' && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Select Approvers
                </p>
                <p className="text-xs text-muted-foreground">
                  Leave is approved when <strong>any one</strong> of these users approves it.
                </p>
                <ApproverPicker
                  staff={staffQuery.data}
                  selected={anyOneApprovers}
                  onChange={setAnyOneApprovers}
                  isError={staffQuery.isError}
                  onRetry={() => void staffQuery.refetch()}
                />
              </div>
            )}

            {method === 'role_based' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Define Approval Steps
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Steps are processed in order. The step marked "Final" triggers leave balance deduction.
                    </p>
                  </div>
                  {steps.length < 4 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addStep}
                      className="shrink-0 ml-3 border-dashed text-muted-foreground hover:text-primary hover:border-primary/40"
                    >
                      <PlusCircle className="w-4 h-4 mr-1.5" />
                      Add Step
                    </Button>
                  )}
                </div>

                {steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3"
                  >
                    <span className="text-xs font-bold text-muted-foreground w-12 shrink-0">
                      Step {step.step_order}
                    </span>

                    <Select value={step.approver_role} onValueChange={(v) => updateRole(idx, v as ApproverRole)}>
                      <SelectTrigger className="flex-1 h-8 text-sm bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <button
                      type="button"
                      onClick={() => setFinal(idx)}
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border transition-colors ${
                        step.is_final
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-background text-muted-foreground border-border hover:text-emerald-600'
                      }`}
                    >
                      <CheckCircle2 className="w-3 h-3" />Final
                    </button>

                    {steps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStep(idx)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                {steps.length > 0 && (
                  <div className="rounded-lg bg-muted/40 border border-border/60 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Preview:</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {steps.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <FlowStepBadge role={s.approver_role} />
                          {i < steps.length - 1 && (
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !canSave}
                className="flex-1"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
